import { useEffect, useState, useRef } from "react";
import { NavLink } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { Mail, MessageCircle, CheckCheck, Plug, RefreshCw, Trash2, ShieldCheck, ExternalLink, AlertTriangle, Copy, Send, Reply, X, Paperclip, FileText, LayoutTemplate } from "lucide-react";
import WhatsAppEmbeddedSignup from "@/components/WhatsAppEmbeddedSignup";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AttachmentPicker({ open, onOpenChange, invoices, quotes, selectedInvoiceIds, selectedQuoteIds, onConfirm, clients }) {
  const [tab, setTab] = useState("invoices");
  const [invSel, setInvSel] = useState(new Set(selectedInvoiceIds));
  const [qSel, setQSel] = useState(new Set(selectedQuoteIds));

  useEffect(() => {
    setInvSel(new Set(selectedInvoiceIds));
    setQSel(new Set(selectedQuoteIds));
  }, [open]); // eslint-disable-line

  const clientName = (id) => clients.find(c => c.client_id === id)?.name || "—";
  const toggle = (set, setSet, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSet(next);
  };
  const totalSel = invSel.size + qSel.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="attach-dialog">
        <DialogHeader>
          <DialogTitle>Allega documenti al messaggio</DialogTitle>
          <DialogDescription>
            Seleziona una o più fatture o preventivi. Verranno generati come PDF e allegati alla mail.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 mb-3 shrink-0">
          {[
            { k: "invoices", label: `Fatture (${invoices.length})`, testid: "attach-tab-invoices" },
            { k: "quotes", label: `Preventivi (${quotes.length})`, testid: "attach-tab-quotes" },
          ].map(t => (
            <button key={t.k} data-testid={t.testid} onClick={() => setTab(t.k)}
              className={`px-4 py-2 rounded-full text-sm ${tab === t.k ? "bg-zen-primary text-white" : "bg-white border border-zen-border text-zen-ink2"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto border border-zen-border rounded-xl">
          {tab === "invoices" ? (
            invoices.length === 0 ? (
              <div className="p-6 text-center text-zen-ink2 text-sm">Nessuna fattura disponibile.</div>
            ) : invoices.map(inv => {
              const on = invSel.has(inv.invoice_id);
              return (
                <button
                  key={inv.invoice_id}
                  data-testid={`attach-invoice-${inv.invoice_id}`}
                  onClick={() => toggle(invSel, setInvSel, inv.invoice_id)}
                  className={`w-full text-left px-4 py-3 border-b border-zen-border flex items-center gap-3 hover:bg-zen-bg transition-colors ${on ? "bg-zen-primary/5" : ""}`}
                >
                  <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${on ? "bg-zen-primary border-zen-primary" : "border-zen-border"}`}>
                    {on && <CheckCheck size={12} className="text-white" strokeWidth={3}/>}
                  </div>
                  <FileText size={16} className="text-zen-primary shrink-0"/>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">Fattura {inv.number}</div>
                    <div className="text-xs text-zen-ink2 truncate">{clientName(inv.client_id)} · {new Date(inv.issued_date).toLocaleDateString("it-IT")}</div>
                  </div>
                  <div className="text-sm font-semibold text-zen-ink shrink-0">€ {Number(inv.amount).toFixed(2)}</div>
                </button>
              );
            })
          ) : (
            quotes.length === 0 ? (
              <div className="p-6 text-center text-zen-ink2 text-sm">Nessun preventivo disponibile.</div>
            ) : quotes.map(q => {
              const on = qSel.has(q.quote_id);
              return (
                <button
                  key={q.quote_id}
                  data-testid={`attach-quote-${q.quote_id}`}
                  onClick={() => toggle(qSel, setQSel, q.quote_id)}
                  className={`w-full text-left px-4 py-3 border-b border-zen-border flex items-center gap-3 hover:bg-zen-bg transition-colors ${on ? "bg-zen-primary/5" : ""}`}
                >
                  <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${on ? "bg-zen-primary border-zen-primary" : "border-zen-border"}`}>
                    {on && <CheckCheck size={12} className="text-white" strokeWidth={3}/>}
                  </div>
                  <FileText size={16} className="text-zen-primary shrink-0"/>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{q.title || `Preventivo ${q.number}`}</div>
                    <div className="text-xs text-zen-ink2 truncate">{q.number} · {clientName(q.client_id)}</div>
                  </div>
                  <Badge variant="outline" className="rounded-full text-xs shrink-0">{q.status}</Badge>
                </button>
              );
            })
          )}
        </div>
        <DialogFooter>
          <div className="text-xs text-zen-ink2 mr-auto">{totalSel} selezionati</div>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button
            data-testid="attach-confirm"
            onClick={() => { onConfirm([...invSel], [...qSel]); onOpenChange(false); }}
            className="bg-zen-primary hover:bg-zen-primaryHover rounded-full"
          >
            <Paperclip size={13} className="mr-1.5"/> Allega ({totalSel})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmailDetailView({ selected, clientName, emailConnected, onSent, invoices, quotes, clients }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [subject, setSubject] = useState(`Re: ${selected.subject || ""}`);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachedInvoiceIds, setAttachedInvoiceIds] = useState([]);
  const [attachedQuoteIds, setAttachedQuoteIds] = useState([]);

  useEffect(() => {
    setSubject(`Re: ${selected.subject || ""}`);
    setText("");
    setReplyOpen(false);
    setAttachedInvoiceIds([]);
    setAttachedQuoteIds([]);
  }, [selected.message_id]);

  const attachedInvoices = invoices.filter(i => attachedInvoiceIds.includes(i.invoice_id));
  const attachedQuotes = quotes.filter(q => attachedQuoteIds.includes(q.quote_id));
  const totalAttachments = attachedInvoiceIds.length + attachedQuoteIds.length;

  const removeInvoice = (id) => setAttachedInvoiceIds(prev => prev.filter(x => x !== id));
  const removeQuote = (id) => setAttachedQuoteIds(prev => prev.filter(x => x !== id));

  const send = async () => {
    if (!text.trim()) return toast.error("Scrivi un messaggio");
    setSending(true);
    try {
      await api.post("/email/send", {
        to: [selected.from_contact],
        subject: subject.trim() || `Re: ${selected.subject || ""}`,
        body: text,
        client_id: selected.client_id || null,
        attach_invoice_ids: attachedInvoiceIds.length ? attachedInvoiceIds : undefined,
        attach_quote_ids: attachedQuoteIds.length ? attachedQuoteIds : undefined,
      });
      toast.success(totalAttachments ? `Email inviata con ${totalAttachments} allegato/i` : "Email inviata");
      setReplyOpen(false);
      setText("");
      setAttachedInvoiceIds([]);
      setAttachedQuoteIds([]);
      onSent?.();
    } catch (e) {
      toast.error(errMsg(e, "Invio fallito"));
    } finally { setSending(false); }
  };

  const openMailto = () => {
    const subj = encodeURIComponent(`Re: ${selected.subject || ""}`);
    const body = encodeURIComponent(`\n\n---\n${selected.body || ""}`);
    window.location.href = `mailto:${encodeURIComponent(selected.from_contact)}?subject=${subj}&body=${body}`;
  };

  return (
    <>
      <div className="flex items-start justify-between mb-6 gap-3 shrink-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-zen-info/20 text-zen-ink rounded-full font-normal">Email</Badge>
            {selected.client_id && clientName(selected.client_id) &&
              <Badge variant="outline" className="rounded-full font-normal">{clientName(selected.client_id)}</Badge>}
            {selected.direction === "out" && <Badge className="bg-zen-primary/15 text-zen-primary rounded-full font-normal">Inviata</Badge>}
          </div>
          <h2 className="font-heading text-2xl mt-3 break-words">{selected.subject || `Messaggio da ${selected.from_name}`}</h2>
          <div className="text-sm text-zen-ink2 mt-1 break-all">
            {selected.direction === "out" ? `A ${selected.to_contact}` : `Da ${selected.from_name} · ${selected.from_contact}`}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {selected.direction !== "out" && (
            emailConnected ? (
              <Button
                data-testid="reply-btn"
                size="sm"
                onClick={()=>setReplyOpen(true)}
                className="bg-zen-primary hover:bg-zen-primaryHover rounded-full"
              >
                <Reply size={14} className="mr-1.5"/> Rispondi
              </Button>
            ) : (
              <Button
                data-testid="reply-btn-mailto"
                size="sm" variant="outline"
                onClick={openMailto}
                className="rounded-full"
                title="Collega l'email per rispondere senza uscire"
              >
                <Mail size={14} className="mr-1.5"/> Rispondi <ExternalLink size={11} className="ml-1"/>
              </Button>
            )
          )}
          {selected.read && <CheckCheck size={18} className="text-zen-primary" title="Letto"/>}
        </div>
      </div>

      <div className="prose prose-sm max-w-none whitespace-pre-wrap text-zen-ink leading-relaxed break-words overflow-y-auto flex-1">
        {selected.body}
      </div>

      {replyOpen && (
        <div className="mt-4 border-t border-zen-border pt-4 space-y-3 shrink-0" data-testid="reply-composer">
          <div className="flex items-center justify-between">
            <span className="zen-label">Nuova risposta</span>
            <button onClick={()=>setReplyOpen(false)} className="text-zen-ink2 hover:text-zen-alert">
              <X size={16}/>
            </button>
          </div>
          <div>
            <Label className="text-xs">Oggetto</Label>
            <Input
              data-testid="reply-subject"
              value={subject}
              onChange={(e)=>setSubject(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">Messaggio</Label>
            <textarea
              data-testid="reply-body"
              rows={5}
              value={text}
              onChange={(e)=>setText(e.target.value)}
              placeholder="Scrivi la tua risposta…"
              className="w-full rounded-xl border border-zen-border bg-white px-3 py-2 text-sm resize-y focus:outline-none focus:border-zen-primary/40 focus:ring-2 focus:ring-zen-primary/10"
            />
          </div>
          {totalAttachments > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="attachments-list">
              {attachedInvoices.map(inv => (
                <div key={inv.invoice_id} data-testid={`attached-invoice-${inv.invoice_id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zen-primary/10 border border-zen-primary/30 text-xs">
                  <FileText size={12} className="text-zen-primary"/>
                  <span>Fattura {inv.number}</span>
                  <button onClick={()=>removeInvoice(inv.invoice_id)} className="text-zen-ink2 hover:text-zen-alert ml-1">
                    <X size={11}/>
                  </button>
                </div>
              ))}
              {attachedQuotes.map(q => (
                <div key={q.quote_id} data-testid={`attached-quote-${q.quote_id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zen-info/10 border border-zen-info/30 text-xs">
                  <FileText size={12} className="text-zen-info"/>
                  <span>Preventivo {q.number}</span>
                  <button onClick={()=>removeQuote(q.quote_id)} className="text-zen-ink2 hover:text-zen-alert ml-1">
                    <X size={11}/>
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2 justify-between items-center">
            <Button
              data-testid="attach-btn"
              variant="outline"
              size="sm"
              onClick={()=>setAttachOpen(true)}
              className="rounded-full"
            >
              <Paperclip size={13} className="mr-1.5"/>
              {totalAttachments > 0 ? `Allegati (${totalAttachments})` : "Allega fattura/preventivo"}
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={()=>setReplyOpen(false)}>Annulla</Button>
              <Button
                data-testid="reply-send"
                onClick={send}
                disabled={sending || !text.trim()}
                className="bg-zen-primary hover:bg-zen-primaryHover rounded-full"
              >
                <Send size={14} className="mr-1.5"/>{sending ? "Invio…" : "Invia"}
              </Button>
            </div>
          </div>
        </div>
      )}
      <AttachmentPicker
        open={attachOpen}
        onOpenChange={setAttachOpen}
        invoices={invoices}
        quotes={quotes}
        clients={clients}
        selectedInvoiceIds={attachedInvoiceIds}
        selectedQuoteIds={attachedQuoteIds}
        onConfirm={(inv, q)=>{ setAttachedInvoiceIds(inv); setAttachedQuoteIds(q); }}
      />
    </>
  );
}

function TemplateSendDialog({ open, onOpenChange, templates, waba, contact, clientId, onSent }) {
  const [selectedName, setSelectedName] = useState("");
  const [bodyParams, setBodyParams] = useState([]);
  const [headerParams, setHeaderParams] = useState([]);
  const [sending, setSending] = useState(false);

  const tpl = templates.find(t => t.name === selectedName);
  const bodyComp = tpl?.components?.find(c => c.type === "BODY");
  const headerComp = tpl?.components?.find(c => c.type === "HEADER");
  const footerComp = tpl?.components?.find(c => c.type === "FOOTER");

  // Count variables in body/header
  const countVars = (text) => {
    if (!text) return 0;
    const m = text.match(/\{\{\s*\d+\s*\}\}/g);
    return m ? new Set(m.map(x => x.replace(/\D/g, ""))).size : 0;
  };
  const nBody = countVars(bodyComp?.text);
  const nHeader = headerComp?.format === "TEXT" ? countVars(headerComp?.text) : 0;

  useEffect(() => {
    setBodyParams(Array(nBody).fill(""));
    setHeaderParams(Array(nHeader).fill(""));
  }, [selectedName]); // eslint-disable-line

  const preview = (() => {
    if (!bodyComp?.text) return "";
    let t = bodyComp.text;
    bodyParams.forEach((v, i) => {
      t = t.replaceAll(`{{${i + 1}}}`, v || `{{${i + 1}}}`);
    });
    return t;
  })();

  const send = async () => {
    if (!selectedName) return toast.error("Seleziona un template");
    if (bodyParams.some(v => !v.trim())) return toast.error("Compila tutte le variabili");
    if (headerParams.some(v => !v.trim())) return toast.error("Compila le variabili header");
    setSending(true);
    try {
      await api.post("/whatsapp/send-template", {
        to: contact,
        template_name: selectedName,
        language: tpl?.language || "it",
        body_params: bodyParams.length ? bodyParams : undefined,
        header_params: headerParams.length ? headerParams : undefined,
        client_id: clientId || null,
      });
      toast.success("Template inviato");
      onSent?.();
      onOpenChange(false);
      setSelectedName("");
    } catch (e) {
      toast.error(errMsg(e, "Invio template fallito"));
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="wa-template-dialog">
        <DialogHeader>
          <DialogTitle>Invia template WhatsApp</DialogTitle>
          <DialogDescription>
            Usa un template Meta approvato per contattare {contact} anche fuori dalla finestra 24h.
          </DialogDescription>
        </DialogHeader>
        {templates.length === 0 ? (
          <div className="zen-card p-6 text-center text-sm text-zen-ink2 bg-zen-bg/50 border-zen-info/20">
            {waba?.waba_id
              ? "Nessun template approvato trovato. Crea e sottometti un template su Meta Business Manager (approvazione ~24h)."
              : "WABA ID mancante — collega WhatsApp o inseriscilo nelle impostazioni per caricare i template."}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>Template approvati ({templates.length})</Label>
              <Select value={selectedName} onValueChange={setSelectedName}>
                <SelectTrigger data-testid="wa-template-select">
                  <SelectValue placeholder="Seleziona un template…"/>
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.name + t.language} value={t.name}>
                      {t.name} <span className="text-zen-ink2 ml-1">· {t.language} · {t.category}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {tpl && (
              <>
                {headerComp?.format === "TEXT" && headerComp.text && (
                  <div className="rounded-xl bg-zen-bg/40 border border-zen-border p-3">
                    <div className="text-xs text-zen-ink2 mb-1">HEADER</div>
                    <div className="text-sm font-medium">{headerComp.text}</div>
                  </div>
                )}
                {headerParams.map((v, i) => (
                  <div key={`hp-${i}`}>
                    <Label className="text-xs">Header variabile {`{{${i + 1}}}`}</Label>
                    <Input
                      data-testid={`wa-header-var-${i + 1}`}
                      value={v}
                      onChange={(e)=>{
                        const next = [...headerParams]; next[i] = e.target.value; setHeaderParams(next);
                      }}
                    />
                  </div>
                ))}
                <div className="rounded-xl bg-zen-bg/40 border border-zen-border p-3">
                  <div className="text-xs text-zen-ink2 mb-1">CORPO (anteprima)</div>
                  <div className="text-sm whitespace-pre-wrap">{preview}</div>
                  {footerComp?.text && (
                    <div className="text-xs text-zen-ink2 italic mt-2">{footerComp.text}</div>
                  )}
                </div>
                {bodyParams.map((v, i) => (
                  <div key={`bp-${i}`}>
                    <Label className="text-xs">Variabile {`{{${i + 1}}}`}</Label>
                    <Input
                      data-testid={`wa-body-var-${i + 1}`}
                      value={v}
                      onChange={(e)=>{
                        const next = [...bodyParams]; next[i] = e.target.value; setBodyParams(next);
                      }}
                      placeholder={`Valore ${i + 1}`}
                    />
                  </div>
                ))}
              </>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={()=>onOpenChange(false)}>Annulla</Button>
          <Button
            data-testid="wa-template-send"
            onClick={send}
            disabled={!selectedName || sending}
            className="bg-zen-primary hover:bg-zen-primaryHover rounded-full"
          >
            <Send size={13} className="mr-1.5"/>{sending ? "Invio…" : "Invia template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WhatsAppThreadView({ selected, clientName, waConnected, waAccount, templates, onSent, loadTemplates }) {
  const [thread, setThread] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const bottomRef = useRef(null);
  const contact = selected.from_contact || selected.to_contact;

  const openTemplates = () => {
    loadTemplates?.();
    setTemplateOpen(true);
  };

  const loadThread = async () => {
    try {
      const { data } = await api.get("/whatsapp/thread", { params: { contact } });
      setThread(data);
    } catch (e) { toast.error(errMsg(e, "Errore caricamento chat")); }
  };
  useEffect(() => { if (contact) loadThread(); /* eslint-disable-next-line */ }, [contact]);
  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [thread]);

  const send = async () => {
    if (!text.trim()) return;
    if (!waConnected) return toast.error("Collega prima WhatsApp Business");
    setSending(true);
    try {
      await api.post("/whatsapp/send", {
        to: contact,
        body: text.trim(),
        client_id: selected.client_id || null,
      });
      setText("");
      await loadThread();
      onSent?.();
    } catch (e) {
      toast.error(errMsg(e, "Invio fallito (finestra 24h scaduta?)"));
    } finally { setSending(false); }
  };

  const fmtTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };
  const fmtDay = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return ""; }
  };

  // Group messages by day
  const days = {};
  thread.forEach((m) => {
    const key = (m.received_at || "").slice(0, 10);
    (days[key] ||= []).push(m);
  });
  const dayKeys = Object.keys(days).sort();

  return (
    <>
      <div className="flex items-start justify-between mb-4 gap-3 shrink-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-zen-primary/15 text-zen-primary rounded-full font-normal">WhatsApp</Badge>
            {selected.client_id && clientName(selected.client_id) &&
              <Badge variant="outline" className="rounded-full font-normal">{clientName(selected.client_id)}</Badge>}
          </div>
          <h2 className="font-heading text-2xl mt-2 break-words">{selected.from_name || contact}</h2>
          <div className="text-sm text-zen-ink2 mt-0.5 break-all">{contact}</div>
        </div>
      </div>

      <div
        className="flex-1 min-h-0 overflow-y-auto -mx-2 px-2 py-3 space-y-4 bg-[#F4EEDD]/40 rounded-xl"
        data-testid="wa-thread"
      >
        {thread.length === 0 ? (
          <div className="text-center text-zen-ink2 text-sm py-10">Nessun messaggio ancora.</div>
        ) : dayKeys.map((day) => (
          <div key={day} className="space-y-2">
            <div className="text-center">
              <span className="text-xs text-zen-ink2 bg-white/70 rounded-full px-3 py-1">
                {fmtDay(days[day][0].received_at)}
              </span>
            </div>
            {days[day].map((m) => (
              <div
                key={m.message_id || m.meta_message_id}
                data-testid={`wa-bubble-${m.direction}`}
                className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}
              >
                <div className={`max-w-[75%] rounded-2xl px-4 py-2 shadow-sm ${
                  m.direction === "out"
                    ? "bg-zen-primary text-white rounded-br-sm"
                    : "bg-white text-zen-ink rounded-bl-sm"
                }`}>
                  <div className="whitespace-pre-wrap break-words text-sm">{m.body}</div>
                  <div className={`text-[10px] mt-1 text-right flex items-center justify-end gap-1 ${
                    m.direction === "out" ? "text-white/70" : "text-zen-ink2"
                  }`}>
                    {fmtTime(m.received_at)}
                    {m.direction === "out" && m.wa_status && (
                      <CheckCheck size={11} className={m.wa_status === "read" ? "text-zen-info" : ""}/>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
        <div ref={bottomRef}/>
      </div>

      {!waConnected ? (
        <div className="mt-4 flex flex-col gap-2 shrink-0">
          <div className="flex gap-2">
            <textarea
              data-testid="wa-quick-msg"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Scrivi il messaggio da mandare via WhatsApp…"
              rows={2}
              className="flex-1 min-w-0 rounded-2xl border border-zen-border bg-white px-4 py-2.5 text-sm resize-none focus:outline-none focus:border-zen-primary/40 focus:ring-2 focus:ring-zen-primary/10"
            />
            <Button
              data-testid="wa-open-mobile"
              onClick={() => {
                const phone = (contact || "").replace(/[^\d]/g, "");
                const url = phone
                  ? `https://wa.me/${phone}${text ? `?text=${encodeURIComponent(text)}` : ""}`
                  : `https://wa.me/?text=${encodeURIComponent(text)}`;
                window.open(url, "_blank", "noopener,noreferrer");
              }}
              className="bg-zen-primary hover:bg-zen-primaryHover rounded-full h-auto shrink-0"
              title="Apri in WhatsApp (funziona anche senza Business)"
            >
              <ExternalLink size={16}/>
            </Button>
          </div>
          <div className="text-[11px] text-zen-ink2 flex items-start gap-1.5">
            <MessageCircle size={12} className="shrink-0 mt-0.5"/>
            <span>
              <b>Nota:</b> con WhatsApp gratuita apri il messaggio in wa.me e lo invii dalla tua app.
              Per rispondere direttamente da FreelanZen, collega <b>WhatsApp Business</b> in Inbox.
            </span>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-4 flex gap-2 shrink-0">
            <textarea
              data-testid="wa-reply-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder="Scrivi un messaggio…"
              rows={2}
              className="flex-1 min-w-0 rounded-2xl border border-zen-border bg-white px-4 py-2.5 text-sm resize-none focus:outline-none focus:border-zen-primary/40 focus:ring-2 focus:ring-zen-primary/10"
            />
            <Button
              data-testid="wa-template-btn"
              onClick={openTemplates}
              variant="outline"
              className="rounded-full h-auto shrink-0 border-zen-primary/30 text-zen-primary hover:bg-zen-primary/5"
              title="Invia un template Meta approvato (funziona anche fuori dalle 24h)"
            >
              <LayoutTemplate size={16}/>
            </Button>
            <Button
              data-testid="wa-reply-send"
              onClick={send}
              disabled={!text.trim() || sending}
              className="bg-zen-primary hover:bg-zen-primaryHover rounded-full h-auto shrink-0"
              title="Invia via Business Cloud API"
            >
              <Send size={16}/>
            </Button>
          </div>
          <div className="text-[10px] text-zen-ink2 mt-1 text-right shrink-0">
            Ricorda: WhatsApp permette messaggi liberi solo nelle 24h dopo l&apos;ultima risposta del cliente.
            Per contattare fuori dalle 24h usa <b>Template</b>.
          </div>
          <TemplateSendDialog
            open={templateOpen}
            onOpenChange={setTemplateOpen}
            templates={templates}
            waba={waAccount}
            contact={contact}
            clientId={selected.client_id}
            onSent={onSent}
          />
        </>
      )}
    </>
  );
}

const PROVIDER_HELP = {  gmail: {
    label: "Gmail",
    steps: [
      { title: "Attiva la verifica in 2 passaggi", url: "https://myaccount.google.com/security", desc: "Serve per generare le password app." },
      { title: "Genera una App Password", url: "https://myaccount.google.com/apppasswords", desc: "Seleziona 'App: Mail', 'Dispositivo: FreelanZen'. Google ti dà 16 caratteri (es. abcd efgh ijkl mnop)." },
      { title: "Incolla email + App Password qui sotto", desc: "Nessuno vede la tua password: viene cifrata prima di essere salvata." },
    ],
  },
  outlook: {
    label: "Outlook / Microsoft 365",
    steps: [
      { title: "Attiva IMAP nell'account Outlook", url: "https://outlook.live.com/mail/0/options/mail/accounts", desc: "Verifica che l'accesso IMAP sia abilitato." },
      { title: "Se hai la 2FA, genera una App Password", url: "https://account.microsoft.com/security", desc: "Sicurezza avanzata → Password app." },
    ],
  },
  yahoo: {
    label: "Yahoo Mail",
    steps: [{ title: "Genera una App Password Yahoo", url: "https://login.yahoo.com/account/security", desc: "Sicurezza account → Genera password app." }],
  },
  aruba: { label: "Aruba", steps: [{ title: "Usa email e password della casella Aruba", desc: "IMAP: imaps.aruba.it:993 (impostato automaticamente)" }] },
  libero: { label: "Libero", steps: [{ title: "Usa email e password Libero", desc: "IMAP: imapmail.libero.it:993" }] },
  custom: { label: "Altro provider (custom)", steps: [{ title: "Server IMAP personalizzato", desc: "Inserisci host e porta del tuo provider (di solito porta 993 SSL)." }] },
};

export default function Inbox() {
  const [messages, setMessages] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("tutti");
  const [account, setAccount] = useState({ connected: false });
  const [waAccount, setWaAccount] = useState({ connected: false });
  const [connectOpen, setConnectOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState(null);

  const [form, setForm] = useState({
    provider: "gmail", email: "", password: "", imap_host: "", imap_port: 993,
  });
  const [waForm, setWaForm] = useState({
    phone_number_id: "", access_token: "", webhook_verify_token: "", app_secret: "", waba_id: "",
  });
  const [wabaEdit, setWabaEdit] = useState("");
  const [waShowManual, setWaShowManual] = useState(false);

  const load = async () => {
    setLoading(true);
    setErrorState(null);
    try {
      const [m, c, a, wa, inv, qs] = await Promise.all([
        api.get("/inbox"),
        api.get("/clients"),
        api.get("/email/account"),
        api.get("/whatsapp/account"),
        api.get("/invoices"),
        api.get("/quotes"),
      ]);
      setClients(c.data);
      setAccount(a.data || { connected: false });
      setWaAccount(wa.data || { connected: false });
      setInvoices(inv.data || []);
      setQuotes(qs.data || []);
      // /inbox can return an object with 'error' or a list
      if (Array.isArray(m.data)) {
        setMessages(m.data);
        if (!selected && m.data.length > 0) setSelected(m.data[0]);
      } else if (m.data && m.data.error) {
        setErrorState(m.data);
        setMessages([]);
      } else {
        setMessages([]);
      }
    } catch (e) {
      toast.error(errMsg(e, "Errore inbox"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const loadTemplates = async () => {
    if (!waAccount.connected) return;
    try {
      const { data } = await api.get("/whatsapp/templates");
      setTemplates(data.templates || []);
    } catch (e) {
      const msg = errMsg(e, "Errore caricamento template");
      toast.error(msg);
      setTemplates([]);
    }
  };

  const saveWabaId = async () => {
    if (!wabaEdit.trim()) return toast.error("Inserisci il WABA ID");
    try {
      const { data } = await api.patch("/whatsapp/account", { waba_id: wabaEdit.trim() });
      setWaAccount(data);
      toast.success("WABA ID salvato");
      setWabaEdit("");
    } catch (e) { toast.error(errMsg(e, "Salvataggio fallito")); }
  };

  const clientName = (id) => clients.find(c=>c.client_id===id)?.name;

  const openMessage = async (m) => {
    setSelected(m);
    if (!m.read) {
      try {
        await api.put(`/inbox/${m.message_id}/read`);
        setMessages((ms) => ms.map(x => x.message_id === m.message_id ? { ...x, read: true } : x));
      } catch { /* silent */ }
    }
  };

  const connect = async () => {
    if (!form.email || !form.password) return toast.error("Compila email e password");
    setLoading(true);
    try {
      await api.put("/email/account", {
        provider: form.provider,
        email: form.email,
        password: form.password,
        imap_host: form.imap_host || null,
        imap_port: parseInt(form.imap_port) || 993,
      });
      toast.success("Casella collegata!");
      setConnectOpen(false);
      setForm({ ...form, password: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e, "Login IMAP fallito. Verifica email + app password."));
    } finally { setLoading(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Scollegare la casella? I messaggi non saranno più sincronizzati.")) return;
    await api.delete("/email/account");
    toast.success("Casella scollegata");
    setAccount({ connected: false });
    setMessages([]);
    setSelected(null);
  };

  const waConnect = async () => {
    if (!waForm.phone_number_id || !waForm.access_token || !waForm.webhook_verify_token) {
      return toast.error("Compila i 3 campi obbligatori");
    }
    setLoading(true);
    try {
      const { data } = await api.put("/whatsapp/account", waForm);
      setWaAccount(data);
      toast.success(`WhatsApp collegato: ${data.display_phone_number || data.phone_number_id}`);
      setWaOpen(false);
      setWaForm({ phone_number_id: "", access_token: "", webhook_verify_token: "", app_secret: "", waba_id: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e, "Verifica Meta fallita"));
    } finally { setLoading(false); }
  };
  const waDisconnect = async () => {
    if (!window.confirm("Scollegare WhatsApp Business? Il webhook Meta smetterà di funzionare finché non lo ricolleghi.")) return;
    await api.delete("/whatsapp/account");
    toast.success("WhatsApp scollegato");
    setWaAccount({ connected: false });
  };
  const copyWebhook = async () => {
    if (!waAccount.webhook_url) return;
    try {
      await navigator.clipboard.writeText(waAccount.webhook_url);
      toast.success("URL webhook copiato");
    } catch {
      window.prompt("Copia questo URL:", waAccount.webhook_url);
    }
  };

  const filtered = messages.filter(m => m.channel !== "whatsapp");
  const unread = filtered.filter(m => !m.read).length;

  return (
    <div className="space-y-6" data-testid="inbox-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="zen-label">Comunicazioni</span>
          <h1 className="mt-2 font-heading text-3xl">Inbox</h1>
          <p className="text-zen-ink2 mt-1">
            {account.connected
              ? <>Connessa a <b>{account.email}</b> · {unread} non letti · ultimo sync {account.last_sync_at ? fmtDate(account.last_sync_at) : "adesso"}</>
              : "Collega la tua casella email per vedere qui i messaggi reali."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {account.connected ? (
            <>
              <Button data-testid="refresh-inbox" variant="outline" onClick={load} disabled={loading} className="rounded-full">
                <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`}/> Aggiorna
              </Button>
              <Button data-testid="disconnect-email" variant="ghost" onClick={disconnect} className="rounded-full text-zen-alert hover:bg-zen-alert/5">
                <Trash2 size={14} className="mr-1.5"/> Scollega email
              </Button>
            </>
          ) : (
            <Button
              data-testid="connect-email-btn"
              onClick={()=>setConnectOpen(true)}
              className="bg-zen-primary hover:bg-zen-primaryHover rounded-full"
            >
              <Mail size={14} className="mr-1.5"/> Collega email
            </Button>
          )}
        </div>
      </div>

      {!account.connected && messages.length === 0 && (
        <div data-testid="inbox-empty-connect" className="zen-card p-8 text-center">
          <Mail size={40} strokeWidth={1.2} className="mx-auto text-zen-primary/40 mb-3"/>
          <h2 className="font-heading text-xl">Collega Gmail, Outlook o Aruba</h2>
          <p className="text-zen-ink2 mt-1 text-sm max-w-lg mx-auto">
            Riceverai qui le email dei tuoi clienti direttamente in FreelanZen. Nessuna password in chiaro — usiamo la app-password del tuo provider, cifrata con Fernet nel nostro DB.
          </p>
          <Button
            onClick={()=>setConnectOpen(true)}
            className="mt-4 bg-zen-primary hover:bg-zen-primaryHover rounded-full"
            data-testid="empty-connect-btn"
          >
            <Plug size={14} className="mr-1.5"/> Collega la mia casella
          </Button>
        </div>
      )}

      {errorState && (
        <div className="zen-card p-4 border-zen-alert/40 bg-zen-alert/5 flex items-start gap-3" data-testid="inbox-error">
          <AlertTriangle size={18} className="text-zen-alert shrink-0 mt-0.5"/>
          <div className="flex-1 text-sm">
            <b>Errore lettura casella</b> — {errorState.detail || "impossibile leggere le email"}. {errorState._hint || ""}
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="outline" onClick={load} className="rounded-full">Riprova</Button>
              <Button size="sm" variant="ghost" onClick={()=>setConnectOpen(true)} className="rounded-full">Ricollega</Button>
            </div>
          </div>
        </div>
      )}

      {(account.connected || messages.length > 0) && (
        <div className="zen-card overflow-hidden">
          <div className="grid md:grid-cols-[380px_1fr] min-h-[500px]">
            <div className="border-r border-zen-border max-h-[650px] overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="p-8 text-center text-zen-ink2 text-sm">
                  {loading ? "Caricamento…" : "Nessun messaggio."}
                </div>
              ) : filtered.map((m) => (
                <button key={m.message_id} onClick={()=>openMessage(m)} data-testid={`msg-${m.message_id}`}
                  className={`w-full text-left px-4 py-4 border-b border-zen-border flex gap-3 hover:bg-zen-bg transition-colors ${selected?.message_id===m.message_id ? "bg-zen-bg" : ""} ${!m.read ? "font-semibold" : ""}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${m.channel==="email" ? "bg-zen-info/20 text-zen-info" : "bg-zen-primary/15 text-zen-primary"}`}>
                    {m.channel === "email" ? <Mail size={16}/> : <MessageCircle size={16}/>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex justify-between items-baseline gap-2">
                      <div className="truncate text-sm">{m.from_name}</div>
                      <div className="text-xs text-zen-ink2 shrink-0">{fmtDate(m.received_at)}</div>
                    </div>
                    <div className="text-sm truncate text-zen-ink mt-0.5">{m.subject || m.body}</div>
                    <div className="text-xs text-zen-ink2 truncate mt-0.5">{m.body}</div>
                  </div>
                  {!m.read && <div className="w-2 h-2 rounded-full bg-zen-primary shrink-0 mt-2"/>}
                </button>
              ))}
            </div>

            <div className="p-6 md:p-8 flex flex-col max-h-[650px]" data-testid="msg-detail">
              {!selected ? (
                <div className="h-full flex items-center justify-center text-zen-ink2">Seleziona un messaggio.</div>
              ) : (
                <EmailDetailView
                  selected={selected}
                  clientName={clientName}
                  emailConnected={account.connected}
                  invoices={invoices}
                  quotes={quotes}
                  clients={clients}
                  onSent={load}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Connect dialog */}
      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="connect-dialog">
          <DialogHeader>
            <DialogTitle>Collega la tua casella email</DialogTitle>
            <DialogDescription>
              Leggeremo solo le email (lettura). Niente invio, niente eliminazione. La password app viene <b>cifrata</b> prima di essere salvata.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Provider</Label>
              <Select value={form.provider} onValueChange={(v)=>setForm({...form, provider: v})}>
                <SelectTrigger data-testid="cf-provider"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {Object.entries(PROVIDER_HELP).map(([k,v])=>(
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Provider help */}
            <div className="zen-card p-4 bg-zen-bg/50 border-zen-info/20">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={15} className="text-zen-primary"/>
                <span className="text-sm font-medium">Come ottenere le credenziali</span>
              </div>
              <ol className="text-sm text-zen-ink2 space-y-1.5 list-decimal ml-5">
                {(PROVIDER_HELP[form.provider]?.steps || []).map((s, i) => (
                  <li key={i}>
                    <span className="text-zen-ink">{s.title}</span>
                    {s.url && (
                      <a href={s.url} target="_blank" rel="noreferrer noopener" className="text-zen-primary hover:underline ml-1.5 inline-flex items-center gap-1">
                        apri <ExternalLink size={11}/>
                      </a>
                    )}
                    {s.desc && <div className="text-xs text-zen-ink2 mt-0.5">{s.desc}</div>}
                  </li>
                ))}
              </ol>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  data-testid="cf-email"
                  type="email"
                  value={form.email}
                  onChange={(e)=>setForm({...form, email: e.target.value})}
                  placeholder="mario@gmail.com"
                />
              </div>
              <div>
                <Label>Password app (16 caratteri per Gmail)</Label>
                <Input
                  data-testid="cf-password"
                  type="password"
                  value={form.password}
                  onChange={(e)=>setForm({...form, password: e.target.value})}
                  placeholder="abcd efgh ijkl mnop"
                />
              </div>
            </div>

            {form.provider === "custom" && (
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label>IMAP host</Label>
                  <Input
                    value={form.imap_host}
                    onChange={(e)=>setForm({...form, imap_host: e.target.value})}
                    placeholder="imap.example.com"
                  />
                </div>
                <div>
                  <Label>Porta</Label>
                  <Input
                    type="number"
                    value={form.imap_port}
                    onChange={(e)=>setForm({...form, imap_port: e.target.value})}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={()=>setConnectOpen(false)}>Annulla</Button>
            <Button
              data-testid="cf-connect"
              onClick={connect}
              disabled={loading}
              className="bg-zen-primary hover:bg-zen-primaryHover"
            >
              {loading ? "Verifico…" : "Collega"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* WhatsApp connect dialog */}
      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="wa-dialog">
          <DialogHeader>
            <DialogTitle>
              {waAccount.connected ? "WhatsApp Business collegato" : "Collega WhatsApp Business (Meta Cloud API)"}
            </DialogTitle>
            <DialogDescription>
              {waAccount.connected
                ? "Configura Meta per puntare al tuo webhook FreelanZen, oppure scollega la connessione."
                : "Usa la Cloud API ufficiale di Meta. Nessun ban, invio & ricezione in tempo reale."}
            </DialogDescription>
          </DialogHeader>

          {waAccount.connected ? (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-zen-primary/5 border border-zen-primary/30">
                <div className="text-sm font-medium text-zen-primary flex items-center gap-1.5">
                  <ShieldCheck size={16}/> Connesso a {waAccount.display_phone_number || waAccount.phone_number_id}
                </div>
                {waAccount.verified_name && (
                  <div className="text-xs text-zen-ink2 mt-1">Nome verificato: <b>{waAccount.verified_name}</b></div>
                )}
                {waAccount.waba_id && (
                  <div className="text-xs text-zen-ink2 mt-1">WABA ID: <b>{waAccount.waba_id}</b></div>
                )}
              </div>
              <div>
                <Label>Webhook URL da configurare in Meta</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input readOnly value={waAccount.webhook_url || ""} className="font-mono text-xs"/>
                  <Button size="sm" variant="outline" onClick={copyWebhook} data-testid="copy-wa-webhook" className="rounded-full">
                    <Copy size={13} className="mr-1"/> Copia
                  </Button>
                </div>
                <p className="text-xs text-zen-ink2 mt-1.5">
                  Meta → App → WhatsApp → Configuration → Webhook: incolla questo URL come Callback URL,
                  usa il tuo Verify Token, e sottoscrivi il campo <b>messages</b>.
                </p>
              </div>
              {!waAccount.waba_id && (
                <div className="rounded-xl bg-zen-info/5 border border-zen-info/30 p-3 space-y-2">
                  <div className="text-sm font-medium">WABA ID (per template)</div>
                  <p className="text-xs text-zen-ink2">
                    Serve per elencare i template Meta approvati. Trova il tuo <b>WhatsApp Business Account ID</b>
                    su Meta → WhatsApp → API Setup (sotto il Phone Number).
                  </p>
                  <div className="flex gap-2">
                    <Input
                      data-testid="wa-waba-id-input"
                      value={wabaEdit}
                      onChange={(e)=>setWabaEdit(e.target.value)}
                      placeholder="es. 1234567890123456"
                    />
                    <Button data-testid="wa-waba-id-save" onClick={saveWabaId} className="bg-zen-primary hover:bg-zen-primaryHover rounded-full">
                      Salva
                    </Button>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="ghost" onClick={()=>setWaOpen(false)}>Chiudi</Button>
                <Button
                  data-testid="wa-disconnect"
                  variant="outline"
                  onClick={waDisconnect}
                  className="text-zen-alert hover:bg-zen-alert/5"
                >
                  <Trash2 size={14} className="mr-1.5"/> Scollega
                </Button>
              </DialogFooter>
            </div>
          ) : !waShowManual ? (
            // ---- Primary flow: Meta Embedded Signup v4 ----
            <div className="space-y-4">
              <WhatsAppEmbeddedSignup
                onConnected={(publicAcc) => {
                  setWaAccount(publicAcc);
                  toast.success("WhatsApp collegato via Meta Embedded Signup");
                  load();
                }}
                onOpenManual={() => setWaShowManual(true)}
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <button
                  data-testid="wa-back-to-es"
                  onClick={() => setWaShowManual(false)}
                  className="text-xs text-zen-primary hover:underline"
                >
                  ← Torna al collegamento automatico (Embedded Signup)
                </button>
              </div>
              <div className="zen-card p-4 bg-zen-bg/50 border-zen-info/20">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck size={15} className="text-zen-primary"/>
                  <span className="text-sm font-medium">Collegamento manuale con token permanente</span>
                </div>
                <p className="text-xs text-zen-ink2">
                  Usa questa modalità solo se hai già generato un System User token in Meta Business Manager.
                  Per la maggior parte degli utenti conviene il collegamento automatico.
                </p>
              </div>

              <div className="grid gap-4">
                <div>
                  <Label>Phone Number ID *</Label>
                  <Input
                    data-testid="wa-phone-id"
                    value={waForm.phone_number_id}
                    onChange={(e)=>setWaForm({...waForm, phone_number_id: e.target.value})}
                    placeholder="1234567890123456"
                  />
                </div>
                <div>
                  <Label>Access Token permanente *</Label>
                  <Input
                    data-testid="wa-token"
                    type="password"
                    value={waForm.access_token}
                    onChange={(e)=>setWaForm({...waForm, access_token: e.target.value})}
                    placeholder="EAAG..."
                  />
                </div>
                <div>
                  <Label>Webhook Verify Token *</Label>
                  <Input
                    data-testid="wa-verify"
                    value={waForm.webhook_verify_token}
                    onChange={(e)=>setWaForm({...waForm, webhook_verify_token: e.target.value})}
                    placeholder="una stringa segreta che sceglierai anche in Meta"
                  />
                  <p className="text-xs text-zen-ink2 mt-1">Almeno 8 caratteri, alfanumerica. Es. <code>zenwa2026sec</code>.</p>
                </div>
                <div>
                  <Label>App Secret (consigliato)</Label>
                  <Input
                    data-testid="wa-secret"
                    type="password"
                    value={waForm.app_secret}
                    onChange={(e)=>setWaForm({...waForm, app_secret: e.target.value})}
                    placeholder="Lascia vuoto per saltare la firma HMAC"
                  />
                </div>
                <div>
                  <Label>WABA ID (opzionale, per i template)</Label>
                  <Input
                    data-testid="wa-waba-id"
                    value={waForm.waba_id}
                    onChange={(e)=>setWaForm({...waForm, waba_id: e.target.value})}
                    placeholder="WhatsApp Business Account ID"
                  />
                  <p className="text-xs text-zen-ink2 mt-1">Lo trovi in Meta → WhatsApp → API Setup. Se lo lasci vuoto, proveremo a rilevarlo dal tuo token.</p>
                </div>
                <p className="text-xs text-zen-ink2">
                  <b>🔒 Privacy:</b> Token e Verify Token vengono cifrati con Fernet prima di salvarli.
                  Il webhook URL definitivo compare qui dopo la connessione.
                </p>
              </div>

              <DialogFooter>
                <Button variant="ghost" onClick={()=>setWaOpen(false)}>Annulla</Button>
                <Button
                  data-testid="wa-connect"
                  onClick={waConnect}
                  disabled={loading}
                  className="bg-zen-primary hover:bg-zen-primaryHover"
                >
                  {loading ? "Verifico…" : "Verifica e collega"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
